<template>
  <div class="AiAccommodationDetailsCard" :data-testid="availabilityRoom.code">
    <div class="AiAccommodationDetailsCard-informations">
      <div class="AiAccommodationDetailsCard-title">
        <ai-typo
          v-if="title"
          :id="'roomCardTitle-' + slugify(title)"
          as="h4"
          variant="heading-03">
          {{ title }}
        </ai-typo>
      </div>

      <div
        id="AiAccommodationDetailsCard-facilities"
        class="AiAccommodationDetailsCard-facilities"
        :aria-label="$t('accessibility.description.roomcard_facilities')"
        role="list">
        <div
          v-for="amenity of amenities"
          :key="amenity.key"
          class="AiAccommodationDetailsCard-facilitiesItem"
          role="listitem">
          <ai-icon
            :namespace="amenity.iconNamespace ?? 'global'"
            :name="(amenity.icon ?? 'help') as GenericIconName"
            class="AiAccommodationDetailsCard-facilitiesItemIcon" />

          <ai-typo
            variant="paragraph-04-regular"
            as="span"
            :aria-label="amenity.label">
            {{ amenity.label }}
          </ai-typo>
        </div>
      </div>

      <div class="AiAccommodationDetailsCard-seeDetails">
        <ai-button
          ref="seeFullDetailsRef"
          slim
          variant="tertiary"
          icon-right="button-arrow-right"
          :label="$t('booking.rooms.seeFullDetails')"
          :aria-label="
            $t('booking.rooms.seeFullDetails', {
              title: title,
            })
          "
          @click="onSeeFullDetailsClick" />
      </div>

      <div class="AiAccomodationDetailsCard-chipsWrapper">
        <all-inclusive-tag
          class="AiAccommodationDetailsCard-inclusiveness"
          :inclusiveness="allInclusiveStatus" />
        <ai-chips
          v-if="isCorporateOffer"
          :label="$t('booking.rooms.corporateOffer')"
          class="AiAccommodationDetailsCard-corporateOffer"
          color="white" />
      </div>

      <!-- Prices -->
      <div class="AiAccommodationDetailsCard-pricesWrapper">
        <ai-typo
          class="AiAccommodationDetailsCard-pricesHeadline"
          as="p"
          variant="paragraph-04-bold">
          {{ $t('booking.rooms.prices.recap.night', numberOfNights) }}
          &nbsp;|&nbsp;
          {{ $t('booking.rooms.prices.recap.guest', numberOfGuests) }}
        </ai-typo>

        <ai-typo
          as="p"
          variant="overline-01"
          color="stratos-800"
          class="AiAccommodationDetailsCard-ratesRange">
          {{ $t('booking.rooms.prices.ratesRange') }}
        </ai-typo>

        <div class="AiAccommodationDetailsCard-prices" role="table">
          <ai-accommodation-details-price
            :is-logged="isLogged"
            :accommodation="accommodation"
            :availability-room="availabilityRoom"
            :user-currency="userCurrency"
            :all-taxes-included="allTaxesIncluded" />
        </div>
      </div>

      <!-- Actions -->
      <div class="AiAccommodationDetailsCard-action">
        <ai-button
          data-testid="booking.room.selectButton"
          :label="$t('booking.rooms.selectRoom')"
          :aria-label="$t('booking.rooms.selectRoom', { title: title })"
          full-width
          @click="$emit('click', availabilityRoom.code ?? 'unknown code')" />
      </div>
    </div>

    <div class="AiAccommodationDetailsCard-carouselWrapper">
      <div class="AiAccommodationDetailsCard-carousel">
        <ai-carousel
          v-if="images && images.length > 1"
          v-model="currentImage"
          hide-gallery
          hide-progress-bar
          hide-navigation-button
          :items="images" />
        <ai-image v-else-if="images && images[0]" v-bind="images[0]?.props" />

        <ai-single-carousel-button
          v-if="images && images.length > 1"
          class="AiAccommodationDetailsCard-carouselNav AiAccommodationDetailsCard-carouselNav--left"
          direction="left"
          @click="previousImage" />
        <ai-single-carousel-button
          v-if="images && images.length > 1"
          class="AiAccommodationDetailsCard-carouselNav AiAccommodationDetailsCard-carouselNav--right"
          direction="right"
          @click="nextImage" />

        <div class="AiAccommodationDetailsCard-carouselControlsMobile">
          <ai-button
            v-if="accommodation?.spacialViewLink"
            as-link
            :url="accommodation?.spacialViewLink"
            target="_blank"
            class="AiAccommodationDetailsCard-carouselControlMobile">
            <ai-icon name="360-view" block />
          </ai-button>
          <button
            v-if="(images?.length ?? 0) > 1"
            ref="galleryMobileRef"
            class="AiAccommodationDetailsCard-carouselControlMobile"
            :aria-label="$t('accessibility.description.buttonViewGallery')"
            @click="galleryOpened = true">
            <ai-icon name="enlarge" block />
          </button>
        </div>
      </div>

      <div class="AiAccommodationDetailsCard-carouselActions">
        <ai-button
          v-if="accommodation?.spacialViewLink"
          :url="accommodation?.spacialViewLink"
          target="_blank"
          variant="tertiary"
          slim
          :label="$t('booking.rooms.360View')"
          class="AiAccommodationDetailsCard-carouselAction"
          icon-left="360-view"
          as-link />

        <ai-button
          v-if="(images?.length ?? 0) > 1"
          ref="galleryRef"
          variant="tertiary"
          slim
          :label="$t('booking.rooms.gallery')"
          class="AiAccommodationDetailsCard-carouselAction"
          icon-left="enlarge"
          :aria-label="$t('accessibility.description.buttonViewGallery')"
          @click="galleryOpened = true" />
      </div>

      <ai-media-gallery
        v-if="(images?.length ?? 0) > 1"
        v-model="galleryOpened"
        v-model:step="currentImage"
        :items="images as AiCarouselItem[]"
        @focus-gallery-button="onFocusGalleryButton" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePreferentialCode } from '~/composables/useIdentificationPlaintext';

import type { GenericIconName } from '../../atoms/AiIcon/types';
import AiImage from '../../atoms/AiImage/AiImage.vue';
import AiCarousel from '../AiCarousel/AiCarousel.vue';
import type { AiCarouselItem } from '../AiCarousel/types';
import AiMediaGallery from '../AiMediaGallery/AiMediaGallery.vue';

import { getAllInclusivePlan } from '~~/domains/booking/helpers/isAllInclusive.helper';
import type {
  Accommodation,
  AvailabilityRoom,
  Hotel,
  RoomOffers,
} from '~~/domains/graphql';
import { getSortedRoomAmenities } from '~~/domains/hotel/helpers';
import { useUserStore } from '~~/domains/user';
import { useBookingStore } from '~~/domains/booking';
import { slugify } from '~~/helpers';

type Props = {
  accommodation?: Accommodation | null;
  availabilityRoom: AvailabilityRoom;
  numberOfNights: number;
  numberOfGuests: number;
  userCurrency?: string;
  allTaxesIncluded?: boolean;
  hotel?: Hotel;
};

type Emits = {
  (event: 'click', productCode: string): void;
  (event: 'see-details', button?: HTMLButtonElement): void;
};

const props = defineProps<Props>();
const emits = defineEmits<Emits>();
const userStore = useUserStore();
const preferentialCode = usePreferentialCode();

const seeFullDetailsRef = ref<ComponentPublicInstance | undefined>();
const galleryRef = ref<ComponentPublicInstance | undefined>();
const galleryMobileRef = ref<HTMLButtonElement | undefined>();

const { t } = useI18n();

const allInclusiveStatus = computed(() =>
  getAllInclusivePlan({
    type: 'offers',
    hotel: props.hotel,
    isLogged: userStore.isLogged,
    offers: props.availabilityRoom.offers as RoomOffers[],
  }),
);

const isSmallDevice = useMatchMediaQuery('tablet');
const currentImage = ref<number>(1);
const images = computed<AiCarouselItem[] | undefined>(() => {
  if (!Array.isArray(props.accommodation?.medias?.photos)) return [];

  return props.accommodation?.medias?.photos.reduce((accumulator, media) => {
    if (!media) return accumulator;

    accumulator.push({
      component: AiImage,
      key: `accomodation-details-${props.availabilityRoom.code}-${media.id}`,
      props: {
        decorative: true,
        height: isSmallDevice.value ? '200px' : '280px',
        src: media.src,
        srcSet: media.srcSet,
      },
    });

    return accumulator;
  }, [] as AiCarouselItem[]);
});
const isLogged = computed(() => userStore.isLogged);

const title = computed(() => props.accommodation?.label);
const amenities = computed(() => {
  if (!props.accommodation) return [];

  return getSortedRoomAmenities(props.accommodation, t);
});

const isCorporateOffer = computed(() => {
  return props.availabilityRoom.offers?.some(
    offer => offer?.code === preferentialCode,
  );
});

function nextImage() {
  if (!images.value) return;

  if (currentImage.value >= images.value.length) {
    currentImage.value = 1;
    return;
  }

  currentImage.value++;
}

function previousImage() {
  if (!images.value) return;

  if (currentImage.value === 1) {
    currentImage.value = images.value.length;
    return;
  }

  currentImage.value--;
}

const galleryOpened = ref<boolean>(false);

const onSeeFullDetailsClick = () => {
  const buttonElement = seeFullDetailsRef.value?.$el;
  emits('see-details', buttonElement);
};

const onFocusGalleryButton = () => {
  const desktopButton = galleryRef.value?.$el;
  const mobileButton = galleryMobileRef.value;

  if (desktopButton && desktopButton.offsetParent !== null) {
    desktopButton.focus();
  }
  if (mobileButton && mobileButton.offsetParent !== null) {
    mobileButton.focus();
  }
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/functions' as func;

.AiAccommodationDetailsCard {
  display: flex;
  flex-direction: column-reverse;
  height: 100%;
  width: 100%;
  padding: constants.$padding-01;
  background-color: colors.$white;

  @media (mq.$from-medium) {
    flex-direction: row-reverse;
    gap: constants.$margin-03;
    padding: constants.$padding-03;
  }
}

.AiAccommodationDetailsCard-carouselWrapper {
  @media (mq.$from-medium) {
    flex-basis: 55%;
  }
}

.AiAccommodationDetailsCard-carouselActions {
  display: none;

  @media (mq.$from-medium) {
    display: block;
  }
}

.AiAccommodationDetailsCard-carouselAction:first-of-type {
  margin-right: constants.$margin-03;
}

.AiAccommodationDetailsCard-carousel {
  position: relative;
  overflow: hidden;
  height: 200px;
  width: 100%;
  margin-bottom: constants.$margin-01;
  background-color: colors.$neutral-600;

  @media (mq.$from-medium) {
    height: 280px;
  }
}

.AiAccommodationDetailsCard-carouselNav {
  position: absolute;
  top: 50%;

  &--left {
    left: constants.$inner-01;
  }

  &--right {
    right: constants.$inner-01;
  }
}

.AiAccommodationDetailsCard-carouselControlsMobile {
  position: absolute;
  bottom: func.calcRem(8);
  left: func.calcRem(8);
  right: func.calcRem(8);

  @media (mq.$from-medium) {
    display: none;
  }
}

.AiAccommodationDetailsCard-carouselControlMobile {
  display: inline-block;
  padding: constants.$inner-02;
  background-color: rgba(colors.$white, 0.8);
  border-radius: constants.$border-radius-full;
  color: colors.$gold-900;
  min-height: unset;
  margin-right: constants.$margin-00;
}

.AiAccommodationDetailsCard-informations {
  @media (mq.$from-medium) {
    flex-basis: 45%;
  }
}

.AiAccommodationDetailsCard-title {
  margin-bottom: constants.$margin-01;
}

.AiAccommodationDetailsCard-facilities {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-00;
    column-gap: constants.$margin-02;
  }
}

.AiAccommodationDetailsCard-facilitiesItem {
  display: flex;

  width: 100%;
  margin-bottom: constants.$margin-00;

  @media (mq.$from-medium) {
    width: calc(50% - constants.$margin-02);
  }
}

.AiAccommodationDetailsCard-facilitiesItemIcon {
  color: colors.$stratos-600;
  margin-right: constants.$margin-00;
}

.AiAccommodationDetailsCard-seeDetails {
  margin-bottom: constants.$margin-02;
}

.AiAccommodationDetailsCard-pricesHeadline {
  margin-bottom: constants.$margin-01;
}

.AiAccommodationDetailsCard-ratesRange {
  margin-bottom: constants.$margin-00;
}

.AiAccommodationDetailsCard-prices {
  margin-bottom: constants.$margin-01;
}

.AiAccommodationDetailsCard-memberChips {
  margin-bottom: constants.$margin-01;
}

.AiAccommodationDetailsCard-inclusiveness {
  margin-bottom: constants.$margin-01;
}

.AiAccommodationDetailsCard-corporateOffer {
  padding: 2rem;
  background: colors.$blue-800 !important;
  text-align: center;
}
.AiAccomodationDetailsCard-chipsWrapper {
  display: flex;
  flex-direction: row;
  gap: constants.$margin-01;
  margin-bottom: constants.$margin-01;
}
</style>
